// ---------------------------------------------------------------------------
// Column data
// ---------------------------------------------------------------------------

export interface BoxPlotColumnData {
  readonly name: string;
  readonly data: number[];
}

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export interface BoxPlotParams {
  readonly columns: BoxPlotColumnData[];
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

export interface BoxPlotDisplayConfig {
  readonly boxColor?: string;
  readonly outlierColor?: string;
  readonly showOutliers?: boolean;
}

export const DEFAULT_BOX_PLOT_DISPLAY: BoxPlotDisplayConfig = {
  boxColor: '#5470c6',
  outlierColor: '#ee6666',
  showOutliers: true,
};

// ---------------------------------------------------------------------------
// Explain mode
// ---------------------------------------------------------------------------

export type BoxPlotElementId =
  | 'minimum'
  | 'lower-whisker'
  | 'q1'
  | 'iqr-box'
  | 'median'
  | 'q3'
  | 'upper-whisker'
  | 'maximum'
  | 'outliers'
  | 'mean';

export interface BoxPlotElementDef {
  readonly id: BoxPlotElementId;
  readonly label: string;
  readonly description: string;
  readonly color: string;
}

export const BOX_PLOT_ELEMENT_DEFS: readonly BoxPlotElementDef[] = [
  { id: 'minimum', label: 'Minimum', description: 'Smallest non-outlier value', color: '#9ca3af' },
  { id: 'lower-whisker', label: 'Lower Whisker', description: 'Line from minimum to Q1', color: '#9ca3af' },
  { id: 'q1', label: 'Q1 (Lower Quartile)', description: '25% of data falls below this value', color: '#8b5cf6' },
  {
    id: 'iqr-box',
    label: 'IQR Box',
    description: 'The box spans Q1 to Q3, containing the middle 50%',
    color: '#8b5cf6',
  },
  { id: 'median', label: 'Median', description: 'The middle value when data is ordered', color: '#14b8a6' },
  { id: 'q3', label: 'Q3 (Upper Quartile)', description: '75% of data falls below this value', color: '#8b5cf6' },
  { id: 'upper-whisker', label: 'Upper Whisker', description: 'Line from Q3 to maximum', color: '#9ca3af' },
  { id: 'maximum', label: 'Maximum', description: 'Largest non-outlier value', color: '#9ca3af' },
  {
    id: 'outliers',
    label: 'Outliers',
    description: 'Values beyond 1.5 × IQR from the nearest quartile',
    color: '#f43f5e',
  },
  { id: 'mean', label: 'Mean', description: 'Arithmetic average of all values', color: '#f43f5e' },
];
